import React, { useEffect, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import {
  AccountGreyIcon,
  CircularDotIcon,
  CompanyIcon,
  FormCloseIcon,
  MailIIcon,
  PencilIcon,
  PhoneIcon
} from 'assets/icons';
import { CustomInput, If, Loader } from 'components';
import { selectCurrentTeam, selectCurrentTeamCustomersPermissions, selectUserDetails } from 'containers/app/appSlice';
import {
  useCreateCustomerMutation,
  useLazyGetCustomersListQuery,
  useUpdateCustomerMutation
} from 'containers/customers/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { debounce } from 'utils/commonUtils';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { CustomerObject, ICustomer } from 'containers/customers/customers.model';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import {
  selectCascadingRelatedEntityData,
  selectEstimateCustomer,
  updateBillingAddress,
  updateEstimateCustomer,
  updateServiceAddress
} from '../../estimatesSlice';
import ServiceAddressSection from './ServiceAddressSection';
import { InvoiceStatus } from 'containers/invoices/invoices.model';

interface EstimateForProps {
  handleOpenCustomerDetailsModal: () => void;
  handleRemoveCustomerFromEstimate: () => void;
  recentCustomers: ICustomer[];
}

const EstimateFor: React.FC<EstimateForProps> = ({
  handleOpenCustomerDetailsModal,
  handleRemoveCustomerFromEstimate,
  recentCustomers
}) => {
  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedCustomer = useAppSelector(selectEstimateCustomer);
  const dispatch = useAppDispatch();
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const userDetails = useAppSelector(selectUserDetails);
  const { relatedInvoiceStatus } = useAppSelector(selectCascadingRelatedEntityData);

  const [getCustomersList, { data: getCustomersResponse, isFetching: isFetchingCustomers }] =
    useLazyGetCustomersListQuery();
  const { customers = [] } = getCustomersResponse ?? {};

  const [createCustomer, { isSuccess: createCustomerSuccess, data: createCustomerData }] = useCreateCustomerMutation();
  const [updateCustomer, { isSuccess: updateCustomerSuccess, data: updatedCustomerData }] = useUpdateCustomerMutation();

  useEffect(() => {
    if (createCustomerSuccess) {
      dispatch(updateEstimateCustomer(createCustomerData));
    }
  }, [createCustomerSuccess, createCustomerData]);

  useEffect(() => {
    if (updateCustomerSuccess) {
      dispatch(updateEstimateCustomer(updatedCustomerData));
    }
  }, [updateCustomerSuccess, updatedCustomerData]);

  const initiateSearch = (searchTerm?: string) => {
    getCustomersList({ search: searchTerm, teamId: currentTeam?.id, page: 0, limit: 20 });
  };

  const debouncedSearchHandler = React.useCallback(debounce(initiateSearch, 500), []);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    if (searchTerm.length > 2) debouncedSearchHandler(searchTerm);
  };

  const onOutSideClick = () => {
    setShowSuggestions(false);
  };

  const handleOpenCustomerModal = () => {
    setShowSuggestions(false);
    setOpenCreateCustomerModal(true);
  };

  const onCustomerClick = customer => {
    setShowSuggestions(false);
    dispatch(updateEstimateCustomer(customer));
    if (customer?.serviceAddress?.length > 0) {
      dispatch(updateServiceAddress(customer.serviceAddress[0]));
    }
    if (customer?.billingAddress) {
      dispatch(updateBillingAddress(customer.billingAddress));
    }
  };

  const onCustomerCreate = customerPayload => {
    if (selectedCustomer && selectedCustomer['ID']) {
      updateCustomer({ ...customerPayload, customerId: selectedCustomer['ID'] });
    } else {
      createCustomer(customerPayload);
    }
    setOpenCreateCustomerModal(false);
  };

  const getCustomerName = () => {
    if (selectedCustomer?.name || selectedCustomer?.lastName) {
      return `${selectedCustomer.name} ${selectedCustomer.lastName}`;
    }
    return '-';
  };

  const createNewCustomerElement = () => {
    return (
      <div
        className="cursor-pointer px-5 pt-5 pb-2.5 text-17px font-semibold text-secondary"
        role="presentation"
        onClick={handleOpenCustomerModal}>
        + Create New Customer {searchText ? `- ${searchText}` : ''}
      </div>
    );
  };

  const renderSuggestions = () => {
    const customersList = searchText.length > 2 ? customers ?? [] : recentCustomers ?? [];
    return (
      <div>
        {createNewCustomerElement()}
        {isFetchingCustomers ? (
          <Loader />
        ) : (
          <div>
            {customersList.map(customer => (
              <div
                onClick={() => onCustomerClick(customer)}
                className="flex cursor-pointer py-2.5 px-5 hover:bg-borderGray"
                key={customer.ID}>
                <div className="text-lg font-semibold leading-5 text-headingGray" key={customer.ID}>
                  {`${customer.name} ${customer.lastName}`}
                </div>

                {customer.email && (
                  <div className={'flex flex-row items-center pl-2'}>
                    <CircularDotIcon />
                    <div className={' pl-3 leading-5 text-primaryText'}>{customer.email}</div>
                  </div>
                )}
                {customer.phoneNumber && (
                  <div className={'flex flex-row items-center pl-2'}>
                    <CircularDotIcon />
                    <div className={' pl-3 leading-5 text-primaryText'}>{customer.phoneNumber}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const isEditCustomerAllowed = () => {
    if (relatedInvoiceStatus && relatedInvoiceStatus !== InvoiceStatus.PENDING) {
      return false;
    }
    if (customerPermissions.editCustomersCreatedByTeam) {
      return true;
    }
    if (customerPermissions.editExistingCustomers && selectedCustomer?.merchantId === userDetails?.id) {
      return true;
    }
    return false;
  };

  const handleAddPhoneNumber = () => {
    if (selectedCustomer.phoneNumber == '' && isEditCustomerAllowed()) {
      handleOpenCustomerModal();
    }
  };

  const handleAddEmail = () => {
    if (selectedCustomer.email == '' && isEditCustomerAllowed()) {
      handleOpenCustomerModal();
    }
  };

  const handleAddCompany = () => {
    if (selectedCustomer.companyName == '' && isEditCustomerAllowed()) {
      handleOpenCustomerModal();
    }
  };

  return (
    <div className="w-full">
      {selectedCustomer ? (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between pb-2.5">
              <div className="text-17px font-semibold text-heading">Estimate for</div>
              <div className="flex items-center gap-4">
                {isEditCustomerAllowed() && (
                  <>
                    <div role="presentation" className="flex items-center" onClick={handleOpenCustomerDetailsModal}>
                      <PencilIcon />
                      <div
                        id="new-estimate-edit-customer-contact"
                        className="ml-2 cursor-pointer text-sbase font-bold text-primaryBtn">
                        Edit {selectedCustomer.name} {selectedCustomer.lastName}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={handleRemoveCustomerFromEstimate}>
                      <FormCloseIcon width={20} height={20} className="cursor-pointer" />
                      <div
                        id="new-estimate-remove-customer-contact"
                        className="cursor-pointer text-sbase font-bold text-primaryBtn">
                        Remove from estimate
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-3 rounded-md border border-borderGray p-4">
              <div className="text-17px font-bold text-primaryText">{getCustomerName()}</div>
              <div className="flex items-center gap-3" onClick={handleAddPhoneNumber} role="presentation">
                <PhoneIcon />
                <div className="break-words">
                  {selectedCustomer.phoneNumber ? (
                    <div className="text-17px text-primaryText">{selectedCustomer.phoneNumber}</div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText ${
                        isEditCustomerAllowed() && 'cursor-pointer'
                      }`}>
                      Add Phone number
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3" onClick={handleAddEmail} role="presentation">
                <MailIIcon />
                <div className=" break-words">
                  {selectedCustomer.email ? (
                    <div className="text-17px text-primaryText">{selectedCustomer.email}</div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText ${
                        isEditCustomerAllowed() && 'cursor-pointer'
                      }`}>
                      Add Email Id
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3" onClick={handleAddCompany} role="presentation">
                <CompanyIcon className="path-fill-current path text-accentText" />
                <div className=" break-words">
                  {selectedCustomer.companyName ? (
                    <div className="text-17px text-primaryText">{selectedCustomer.companyName}</div>
                  ) : (
                    <div
                      className={`text-sbase font-normal italic text-accentText ${
                        isEditCustomerAllowed() && 'cursor-pointer'
                      }`}>
                      Add company
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <ServiceAddressSection isEditable={isEditCustomerAllowed()} />
        </>
      ) : (
        <OutsideClickHandle onOutsideClick={onOutSideClick}>
          <div className="w-full">
            <Popover className="relative z-10">
              {() => (
                <>
                  <div className="w-full">
                    <CustomInput
                      value={searchText}
                      id="estimate-for-input"
                      onChange={onSearchTermChange}
                      autoComplete="off"
                      placeholder="Type customer name"
                      suffix={<AccountGreyIcon />}
                      hasSuffix={true}
                      suffixStyle="absolute inset-y-0 right-0 flex items-center pr-3"
                      showSuffixAlways={true}
                      className="focus:border-none focus:outline-none"
                      onFocus={() => setShowSuggestions(true)}
                    />
                  </div>

                  <Transition
                    show={showSuggestions}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1">
                    <Popover.Panel
                      className="customNormalScroll absolute z-40 mt-[2px] max-h-36 w-full overflow-y-auto rounded-md
                        border border-borderGray bg-primary shadow-md">
                      {renderSuggestions()}
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>
        </OutsideClickHandle>
      )}
      <If condition={Boolean(openCreateCustomerModal)}>
        <CreateCustomer
          open={openCreateCustomerModal}
          onClose={() => setOpenCreateCustomerModal(false)}
          panelType={CreateCustomerModalTypes.CREATE_CUSTOMER}
          customerData={selectedCustomer as CustomerObject}
          customerDataFetchSuccess={true}
          customerDataFetchLoading={false}
          onAddCustomer={onCustomerCreate}
          defaultName={searchText}
        />
      </If>
    </div>
  );
};

export default EstimateFor;
